/**
 * Archivos Útiles — Gestión documental del módulo Ingeniería > Proyectos.
 * Sub-secciones (NORMAS / DISEÑOS TÉCNICOS / DOCUMENTOS VARIOS por defecto)
 * + archivos de cualquier formato persistidos en Mongo.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FolderOpen, Folder, Plus, Upload, Download, Eye, Trash2, Pencil, Loader2,
  FileText, FileImage, FileSpreadsheet, FileArchive, File as FileIcon, Check, X,
} from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";

const opts = { withCredentials: true };

function fileIcon(ext) {
  const e = (ext || "").toLowerCase();
  if (["pdf"].includes(e)) return { Icon: FileText, color: "#DC2626", bg: "#FEE2E2" };
  if (["doc", "docx"].includes(e)) return { Icon: FileText, color: "#2563EB", bg: "#DBEAFE" };
  if (["xls", "xlsx", "csv"].includes(e)) return { Icon: FileSpreadsheet, color: "#16A34A", bg: "#DCFCE7" };
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(e)) return { Icon: FileImage, color: "#7C3AED", bg: "#EDE9FE" };
  if (["zip", "rar", "7z", "tar", "gz"].includes(e)) return { Icon: FileArchive, color: "#D97706", bg: "#FEF3C7" };
  return { Icon: FileIcon, color: "#475569", bg: "#F1F5F9" };
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function ProyectosArchivos() {
  const [subsecciones, setSubsecciones] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newSubName, setNewSubName] = useState("");
  const [addingSub, setAddingSub] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/proyectos/archivos`, opts);
      const subs = data.subsecciones || [];
      setSubsecciones(subs);
      setActiveId((prev) => (prev && subs.some((s) => s._id === prev)) ? prev : (subs[0]?._id || null));
    } catch (_e) {
      toast.error("Error al cargar archivos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = subsecciones.find((s) => s._id === activeId) || null;

  const handleCreateSub = async () => {
    const nombre = newSubName.trim();
    if (!nombre) return;
    try {
      const { data } = await axios.post(`${API}/proyectos/archivos/subseccion`, { nombre }, opts);
      toast.success("Sub-sección creada");
      setNewSubName("");
      setAddingSub(false);
      await load();
      setActiveId(data._id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear");
    }
  };

  const handleRename = async (sub) => {
    const nombre = renameVal.trim();
    if (!nombre) { setRenamingId(null); return; }
    try {
      await axios.put(`${API}/proyectos/archivos/subseccion/${sub._id}`, { nombre }, opts);
      toast.success("Sub-sección renombrada");
      setRenamingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo renombrar");
    }
  };

  const handleDeleteSub = async (sub) => {
    const msg = (sub.archivos?.length || 0) > 0
      ? `¿Eliminar la sub-sección "${sub.nombre}" y sus ${sub.archivos.length} archivo(s)? Esta acción no se puede deshacer.`
      : `¿Eliminar la sub-sección "${sub.nombre}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/proyectos/archivos/subseccion/${sub._id}`, opts);
      toast.success("Sub-sección eliminada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !active) return;
    const form = new FormData();
    form.append("subseccion_id", active._id);
    files.forEach((f) => form.append("files", f));
    setUploading(true);
    try {
      await axios.post(`${API}/proyectos/archivos/upload`, form, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} archivo(s) subido(s)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`¿Eliminar el archivo "${file.filename}"?`)) return;
    try {
      await axios.delete(`${API}/proyectos/archivos/${file._id}`, opts);
      toast.success("Archivo eliminado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const openFile = (file, inline) => {
    window.open(`${API}/proyectos/archivos/${file._id}/download${inline ? "?inline=1" : ""}`, "_blank");
  };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="archivos-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <FolderOpen size={22} className="text-[#8B5CF6]" /> Archivos Útiles
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Normas, documentos, diseños, planos y plantillas del equipo de ingeniería — accesibles desde cualquier sesión.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar de sub-secciones */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 h-fit" data-testid="archivos-subsecciones">
          <div className="flex items-center justify-between px-2 pb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Sub-secciones</span>
          </div>
          <div className="space-y-0.5">
            {subsecciones.map((sub) => (
              <div key={sub._id} className="group">
                {renamingId === sub._id ? (
                  <div className="flex items-center gap-1 px-1.5 py-1">
                    <input
                      autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleRename(sub); if (e.key === "Escape") setRenamingId(null); }}
                      className="flex-1 min-w-0 px-2 py-1 text-[12.5px] rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B5CF6]"
                      data-testid="subseccion-rename-input"
                    />
                    <button onClick={() => handleRename(sub)} className="p-1 rounded text-emerald-600 hover:bg-emerald-50" data-testid="subseccion-rename-save"><Check size={14} /></button>
                    <button onClick={() => setRenamingId(null)} className="p-1 rounded text-gray-400 hover:bg-gray-100"><X size={14} /></button>
                  </div>
                ) : (
                  <div
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-xl cursor-pointer transition-all ${
                      activeId === sub._id ? "bg-[#8B5CF6]/10 text-[#6D28D9]" : "text-gray-600 hover:bg-gray-50"
                    }`}
                    onClick={() => setActiveId(sub._id)}
                    data-testid={`subseccion-item-${sub._id}`}
                  >
                    {activeId === sub._id ? <FolderOpen size={15} /> : <Folder size={15} />}
                    <span className="flex-1 min-w-0 truncate text-[12.5px] font-semibold">{sub.nombre}</span>
                    <span className="text-[10px] text-gray-400 font-medium">{sub.archivos?.length || 0}</span>
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button onClick={(e) => { e.stopPropagation(); setRenamingId(sub._id); setRenameVal(sub.nombre); }}
                        className="p-0.5 rounded text-gray-400 hover:text-blue-600" title="Renombrar"
                        data-testid={`subseccion-rename-${sub._id}`}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteSub(sub); }}
                        className="p-0.5 rounded text-gray-400 hover:text-red-600" title="Eliminar"
                        data-testid={`subseccion-delete-${sub._id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Nueva sub-sección */}
          <div className="mt-2 pt-2 border-t border-gray-100">
            {addingSub ? (
              <div className="flex items-center gap-1 px-1.5">
                <input
                  autoFocus value={newSubName} onChange={(e) => setNewSubName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateSub(); if (e.key === "Escape") { setAddingSub(false); setNewSubName(""); } }}
                  placeholder="Nombre..." className="flex-1 min-w-0 px-2 py-1.5 text-[12.5px] rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B5CF6]"
                  data-testid="new-subseccion-input"
                />
                <button onClick={handleCreateSub} className="p-1 rounded text-emerald-600 hover:bg-emerald-50" data-testid="new-subseccion-save"><Check size={15} /></button>
                <button onClick={() => { setAddingSub(false); setNewSubName(""); }} className="p-1 rounded text-gray-400 hover:bg-gray-100"><X size={15} /></button>
              </div>
            ) : (
              <button onClick={() => setAddingSub(true)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-xl text-[12.5px] font-semibold text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-all"
                data-testid="new-subseccion-btn">
                <Plus size={15} /> Nueva sub-sección
              </button>
            )}
          </div>
        </div>

        {/* Panel de archivos */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="archivos-panel">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-[15px] font-bold text-gray-800">{active?.nombre || "—"}</h2>
            <label className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white shadow-sm transition-all cursor-pointer ${
              uploading || !active ? "bg-gray-300 cursor-not-allowed" : "bg-[#8B5CF6] hover:brightness-110"
            }`} data-testid="upload-btn">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              <span>{uploading ? "Subiendo..." : "Subir archivo"}</span>
              <input ref={fileInputRef} type="file" multiple className="hidden" disabled={uploading || !active}
                onChange={handleUpload} data-testid="file-input" />
            </label>
          </div>

          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader2 size={20} className="mx-auto animate-spin" /></div>
          ) : !active || (active.archivos?.length || 0) === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3 bg-[#8B5CF6]/10 text-[#8B5CF6]">
                <FolderOpen size={22} />
              </div>
              <h4 className="text-[14px] font-semibold text-gray-700">Sin archivos en esta sub-sección</h4>
              <p className="text-[12.5px] text-gray-400 mt-1">Subí normas, planos o documentos con el botón "Subir archivo".</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {active.archivos.map((f) => {
                const { Icon, color, bg } = fileIcon(f.ext);
                return (
                  <div key={f._id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/60" data-testid={`archivo-row-${f._id}`}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: bg, color }}>
                      <Icon size={17} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{f.filename}</p>
                      <p className="text-[11px] text-gray-400">
                        {formatSize(f.size)} · {formatDate(f.uploaded_at)} · {f.uploaded_by || "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openFile(f, true)} className="p-1.5 rounded-lg text-gray-500 hover:bg-violet-50 hover:text-[#8B5CF6]" title="Ver" data-testid={`archivo-view-${f._id}`}>
                        <Eye size={15} />
                      </button>
                      <button onClick={() => openFile(f, false)} className="p-1.5 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600" title="Descargar" data-testid={`archivo-download-${f._id}`}>
                        <Download size={15} />
                      </button>
                      <button onClick={() => handleDeleteFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600" title="Eliminar" data-testid={`archivo-delete-${f._id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
